import axios from "axios";
import { ApiResponse } from "@/types/api";
import { Product } from "@/types/broadbandProduct";
import { Handset, HandsetDeal } from "@/types/index";
import { handleApiError } from "@/utils/formatApiError";

export const getProducts = async (
	postcode?: string
): Promise<ApiResponse<Array<Product> | null>> => {
	try {
		let url = `${process.env.API_ENDPOINT}/api/products`;

		if (postcode) {
			const formattedPostcode = postcode.replace(/\s/g, "");
			url += `/?postcode=${formattedPostcode}`;
		}

		const response = await axios({
			url,
			method: "GET",
		});

		const products = response.data.data.map((product: any) => {
			return {
				...product,
				download_speed: Number(product.download_speed),
				monthly_cost: Number(product.monthly_cost).toFixed(2),
			};
		});

		return {
			data: products,
			error: null,
		};
	} catch (e: any) {
		return {
			data: null,
			error: handleApiError(e),
		};
	}
};

export const getHandsets = async (): Promise<
	ApiResponse<Array<Handset>> | ApiResponse<null>
> => {
	try {
		const response = await axios({
			url: `${process.env.API_ENDPOINT}/api/handsets`,
			method: "GET",
			headers: {
				Authorization: `${process.env.API_TOKEN}`,
				Accept: "application/json",
			},
		});

		return {
			data: response.data.data,
			error: null,
		};
	} catch (e: any) {
		return {
			data: null,
			error: handleApiError(e),
		};
	}
};

export const getMobileProducts = async (
	brandsFilters: Array<string | number>,
	networkFilters: Array<string | number>,
	monthlyFilters: Array<string | number>,
	upFrontCostFilters: Array<string | number>,
	dataFilters: Array<string | number>,
	minutesFilters: Array<string | number>,
	contractFilters: Array<string | number>,
	sorter: string,
	page: number,
	selectedColour: string,
	selectedStorage: string,
	name?: string,
	model?: string,
	simOnly?: boolean
): Promise<
	| ApiResponse<{
			data: Array<HandsetDeal>;
			links: {
				first: string | null;
				last: string | null;
				prev: string | null;
				next: string | null;
			};
			meta: {
				current_page: number;
				from: number;
				path: string;
				per_page: number;
				to: number;
			};
	  }>
	| ApiResponse<null>
> => {
	const baseUrl = simOnly
		? `${window.location.origin}/api/sim-only`
		: `${window.location.origin}/api/handsets/deals`;

	let url = new URL(baseUrl);

	if (brandsFilters) {
		for (let i = 0; i < brandsFilters.length; i++) {
			url.searchParams.append(
				`filters[brands][${i}]`,
				brandsFilters[i].toString()
			);
		}
	}

	if (networkFilters) {
		for (let i = 0; i < networkFilters.length; i++) {
			url.searchParams.append(
				`filters[networks][${i}]`,
				networkFilters[i].toString()
			);
		}
	}

	if (monthlyFilters) {
		for (let i = 0; i < monthlyFilters.length; i++) {
			url.searchParams.append(`monthly_cost`, `0-${monthlyFilters[i]}`);
		}
	}

	if (upFrontCostFilters) {
		for (let i = 0; i < upFrontCostFilters.length; i++) {
			url.searchParams.append(`upfront_cost`, `0-${upFrontCostFilters[i]}`);
		}
	}

	if (dataFilters) {
		for (let i = 0; i < dataFilters.length; i++) {
			url.searchParams.append(`data`, `${dataFilters[i]}`);
		}
	}

	if (minutesFilters) {
		for (let i = 0; i < minutesFilters.length; i++) {
			url.searchParams.append(`minutes`, `${minutesFilters[i]}`);
		}
	}

	if (contractFilters) {
		for (let i = 0; i < contractFilters.length; i++) {
			url.searchParams.append(`contract_length`, `${contractFilters[i]}`);
		}
	}

	sorter !== "" && url.searchParams.append(`sorter`, `${sorter}`);

	page !== 1 && url.searchParams.append("page", `${page}`);

	if (name !== undefined && name !== "") {
		url.searchParams.append(`product_name`, `${name}`);
	}

	if (model !== undefined && model !== "") {
		url.searchParams.append(`model`, `${model}`);
	}

	selectedColour !== "" &&
		url.searchParams.append(`colour`, `${selectedColour}`);

	selectedStorage !== "" &&
		url.searchParams.append(`storage`, `${selectedStorage}`);

	try {
		const response = await axios({
			url: url.toString(),
			method: "GET",
			maxBodyLength: Infinity,
		});

		return {
			data: response.data,
			error: null,
		};
	} catch (e: any) {
		return {
			data: null,
			error: handleApiError(e),
		};
	}
};

export const getPhoneSpec = async (
	name: string,
	model: string
): Promise<
	ApiResponse<{
		colours: Array<string>;
		storage: Array<string>;
	} | null>
> => {
	try {
		const response = await axios({
			url: `${process.env.API_ENDPOINT}/api/handsets/options?product_name=${name}&model=${model}`,
			method: "GET",
			headers: {
				Authorization: `${process.env.API_TOKEN}`,
				Accept: "application/json",
			},
		});

		return {
			data: response.data.data,
			error: null,
		};
	} catch (e: any) {
		return {
			data: null,
			error: handleApiError(e),
		};
	}
};
